import { useState } from 'react';
import jwt_decode from 'jwt-decode';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Swal from 'sweetalert2';
import { SignatureCreator } from '../../../../lease/components/steps/components';

const TenantDetails = ({ formik }) => {
  const pathParts = window.location.pathname.split('/');
  const urlToken = pathParts[pathParts.length - 1];
  const { tenantId } = jwt_decode(urlToken);

  const [isSignatureCreatorOpen, setIsSignatureCreatorOpen] = useState(false);
  const [currentTenantIndex, setCurrentTenantIndex] = useState(null);

  const handleCellClick = async (index) => {
    const clickedTenant = formik.values.tenants[index];

    if (String(clickedTenant.id) !== String(tenantId)) {
      Swal.fire({
        icon: 'error',
        title: 'Access Denied',
        text: "You are not authorized to sign this tenant's section.",
      });
      return;
    }

    const { isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Before you sign',
      text: 'Please ensure you have read all pages carefully. By signing, your digital signature will be recorded and your initials will be auto-filled on all required pages. Your signature is legally binding.',
      showCancelButton: true,
      confirmButtonText: 'I have read & agree',
      cancelButtonText: 'Cancel',
      focusCancel: true,
    });

    if (!isConfirmed) return;

    setCurrentTenantIndex(index);
    setIsSignatureCreatorOpen(true);
  };

  const handleSignatureCreated = ({ signatureData }) => {
    if (signatureData && currentTenantIndex !== null) {
      const today = new Date().toLocaleDateString("en-CA");
      const updatedTenants = [...formik.values.tenants];
      updatedTenants[currentTenantIndex] = {
        ...updatedTenants[currentTenantIndex],
        signature: signatureData,
        date: today,
      };

      formik.setFieldValue('tenants', updatedTenants);
    }
    setIsSignatureCreatorOpen(false);
    setCurrentTenantIndex(null);
  };

  return (
    <Box className="section">
      <Typography variant="subtitle2" className="section-title">TENANTS AGREEMENT:</Typography>
      <Typography variant="body2" className="section-content" sx={{ mb: 1 }}>
        The tenant agrees that in addition to the occupants, the premises may be occupied only by the following persons:
      </Typography>

      <Paper variant="outlined" className="tenant-details-table" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            {formik.values.tenants?.length > 0 ? (
              formik.values.tenants.map((tenant, index) => (
                <TableRow key={index}>
                  <TableCell className="label">Name</TableCell>
                  <TableCell className="value">
                    {tenant.name || '-'}
                  </TableCell>
                  <TableCell className="label">Signature</TableCell>
                  <TableCell className="value" onClick={() => handleCellClick(index)} sx={{ p: '1px' }}>
                    {tenant.signature ? (
                      <Box
                        sx={{
                          position: 'relative',
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          padding: '1px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 20,
                          cursor: 'pointer',
                          backgroundColor: '#fafafa',
                        }}
                      >
                        <img
                          src={tenant.signature}
                          alt="Signature"
                          style={{ maxHeight: '20px', objectFit: 'contain' }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          border: '1px dashed #bbb',
                          borderRadius: 1,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#888',
                          fontStyle: 'italic',
                          cursor: 'pointer',
                          backgroundColor:
                            String(tenant.id) === String(tenantId) ? '#fffbe6' : '#fcfcfc',
                          boxShadow:
                            String(tenant.id) === String(tenantId)
                              ? '0 0 0 2px #ffcc80'
                              : 'none',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Click to sign
                      </Box>
                    )}
                  </TableCell>
                  <TableCell className="label">Date</TableCell>
                  <TableCell className="value">
                    {tenant.date || (tenant.signature ? 'Date will be filled when signed' : 'Automatically filled when signed')}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: '#aaa' }}>
                  No tenants available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Signature Creator Modal */}
      {currentTenantIndex !== null && (
        <SignatureCreator
          isOpen={isSignatureCreatorOpen}
          onClose={() => setIsSignatureCreatorOpen(false)}
          onSign={handleSignatureCreated}
          onSignAndSave={handleSignatureCreated}
          initialSignature={formik.values.tenants[currentTenantIndex]?.signature || ''}
          signaturePath={`tenants[${currentTenantIndex}].signature`}
          formik={formik}
          width={600}
          height={200}
        />
      )}
    </Box>
  );
};

const LandlordDetails = ({ formik }) => {
  const manager = formik.values?.manager || {
    first_name: "",
    last_name: "",
    phone_no: "",
    email: "",
    signature: "",
    date: "",
  };

  return (
    <Box className="section">
      <Typography variant="subtitle2" className="section-title">
        LANDLORD/LANDLORD AGENT:
      </Typography>

      <Paper variant="outlined" className="landlord-details-table" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label">Name</TableCell>
              <TableCell className="value">
                {(manager.first_name || '') + ' ' + (manager.last_name || '') || '-'}
              </TableCell>
              <TableCell className="label">Signature</TableCell>
              <TableCell className="value">
                {manager.signature ? (
                  <Box sx={{ height: 20 }}>
                    <img
                      src={manager.signature}
                      alt="Signature"
                      style={{ maxWidth: '100%', maxHeight: '20px' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 20,
                      border: '1px solid #eee',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#aaa',
                      fontStyle: 'italic',
                    }}
                  >
                    Awaiting signature
                  </Box>
                )}
              </TableCell>
              <TableCell className="label">Date</TableCell>
              <TableCell className="value">{manager.date || '-'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const BuildingAddressDetails = ({ formik }) => {
  const formatAddress = () => {
    const { property_details } = formik.values;

    if (!property_details) return '';

    const { building_name, address, postal_code, province, city } = property_details;
    const mainAddress = address || building_name;
    const addressParts = [mainAddress, postal_code, province, city];

    return addressParts.filter(Boolean).join(', ');
  };

  const phone = formik.values.manager?.phone_no || 'N/A';
  const email = formik.values.manager?.email || 'N/A';

  return (
    <Box className="section">
      <Typography variant="subtitle2" className="section-title">BUILDING ADDRESS AND CONTACT:</Typography>

      <Paper variant="outlined" className="building-details-table" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell className="label" sx={{ width: '15%' }}>
                Address:
              </TableCell>
              <TableCell className='value' sx={{ width: '45%' }}>
                <Box sx={{ mb: 1 }}>
                  {formatAddress() || <span style={{ color: '#aaa' }}>No address available</span>}
                </Box>
              </TableCell>
              <TableCell className="label-2" sx={{ width: '15%' }}>Phone & Email:</TableCell>
              <TableCell className='value-2' sx={{ width: '25%' }}>
                <Box sx={{ mb: 0.5 }}>{phone}</Box>
                <Box>{email}</Box>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const LeaseTermSection = ({ title, content }) => (
  <Box className="section">
    <Typography variant="subtitle2" className="section-title">{title}</Typography>
    <Typography variant="body2" className="section-content">
      {content}
    </Typography>
  </Box>
);

const Step6 = ({ formik, electronicSignatureConsent, setElectronicSignatureConsent }) => {
  const leaseSections = [
    {
      title: "22. LANDLORD'S AGENT",
      content: "It is understood and agreed by and between the parties hereto that the landlord shall have the right to appoint such agents to act on his behalf, and deal with the tenant pursuant to this lease, as the landlord shall consider necessary and for the lease."
    },
    {
      title: "23. LOCKS, KEYS, PARKING PASS, FOBS",
      content: "The cost of replacing any lost suite keys, Fobs, Mailbox keys, Parking Pass will be borne by the tenant. No locks are to be installed or changed by the tenant. All keys to the premises and related facilities shall be delivered up to the landlord upon termination of this lease. If the landlord is required to change any locks due to keys not being delivered promptly to the landlord, the tenant shall immediately pay for all costs incurred. (Tenants are responsible for the cost of any lock outs- $250 one-time charge). If the Management supplies you with a replacement item, the charge to the tenant will be $150.00 per item. Full payment must be made in full prior to the landlord providing replacement item to the tenant."
    },
    {
      title: "24. HEAT AND HOT WATER",
      content: "In the event of an interruption of heat or hot water due to mechanical failure, boiler malfunction, breakdown, circumstances beyond the Landlord’s control, or required repairs, the Landlord shall take reasonable steps to restore service within a reasonable time after becoming aware of the issue. The Landlord shall not be liable for indirect or consequential damages, including alternate accommodation costs, except where such liability arises from the Landlord’s failure to meet obligations under the Residential Tenancies Act (Alberta)."
    },
    {
      title: "25. WAIVER TERMS & ACCEPTANCE",
      content: "The waiver of any terms of this contract at any time shall not be deemed a waiver on any occasion or of any other term. Any violation of these conditions shall constitute a breach of the tenancy, and the landlord shall thereafter be entitled to possession after giving a 14 day notice under section 29(1) of RTA."
    },
    {
      title: "26. ACCEPTANCE OF LEASE",
      content: "The Tenant does hereby accept the lease of the Premises to be held as tenant, subject to the conditions, restrictions and covenants contained within this Agreement or attached hereto."
    }
  ];

  const handleCheckboxChange = (event) => {
    setElectronicSignatureConsent(event.target.checked);
  };

  return (
    <Box className="lease-form-step6">
      {leaseSections.map((section, index) => (
        <LeaseTermSection
          key={index}
          title={section.title}
          content={section.content}
        />
      ))}

      <Box className="signature">
        <span className="notice">
          IF THERE IS MORE THAN ONE TENANT, THEIR LIABILITY SHALL BE JOINT IN SEVERAL.
        </span>
        <br />
        <span className="notice">
          THE DATE OF THIS AGREEMENT SHALL BE THE DATE THAT THE LAST OF THE PARTIES HAVE SIGNED.
        </span>
        <br />
        <TenantDetails formik={formik} />
        <LandlordDetails formik={formik} />
        <BuildingAddressDetails formik={formik} />
        <Box className="section" sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={electronicSignatureConsent}
                onChange={handleCheckboxChange}
                name="consentToElectronicSignature"
                sx={{
                  color: electronicSignatureConsent ? 'primary.main' : 'error.main',
                  '& .MuiSvgIcon-root': {
                    border: electronicSignatureConsent ? 'none' : '1px solid red',
                    borderRadius: '4px',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#555' }}>
                By proceeding, I consent to the use of electronic communications, electronic signatures, and the features of this online system for the creation, signing, and management of this lease agreement. I acknowledge that my electronic signature is legally binding and equivalent to a handwritten signature under applicable law.
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export { Step6 };